import * as React from "react";
import { graphql } from "gatsby";

import { Layout } from "../components/Layout";
import { StoreButtons } from "../components/StoreButtons";
import {
  ExplainerShell,
  ReviewedDate,
  Sources,
  RelatedGuides,
  Disclaimer,
} from "../components/explainer/ExplainerParts";
import {
  articleSchema,
  breadcrumbSchema,
  faqSchema,
} from "../utils/structuredData";

// On a page marked faq: true every H2 is a question and the text beneath it
// the answer, so the FAQPage structured data comes from the same Markdown.
const stripTags = (fragment) =>
  fragment
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const faqFromHtml = (html) =>
  html
    .split(/(?=<h2)/)
    .filter((section) => section.startsWith("<h2"))
    .map((section) => {
      const [, heading, body] = section.match(/<h2[^>]*>(.*?)<\/h2>([\s\S]*)/);
      return { question: stripTags(heading), answer: stripTags(body) };
    })
    .filter(({ question, answer }) => question && answer);

// An explainer page written as Markdown in content/explainers.
const ExplainerPage = ({ data, location, pageContext }) => {
  const { slug, navItems } = pageContext;
  const { frontmatter, html } = data.markdownRemark;

  return (
    <Layout
      metaTitle={frontmatter.metaTitle}
      metaDescription={frontmatter.metaDescription}
      ogType="article"
      structuredData={[
        articleSchema({
          slug,
          headline: frontmatter.title,
          description: frontmatter.metaDescription,
          datePublished: frontmatter.datePublished,
          dateModified: frontmatter.lastReviewed,
        }),
        breadcrumbSchema({ slug, name: frontmatter.title }),
        frontmatter.faq && faqSchema(faqFromHtml(html)),
      ]}
    >
      <ExplainerShell navItems={navItems} slug={slug} location={location}>
        <h1 className="text-2xl md:text-3xl font-semibold">
          {frontmatter.title}
        </h1>
        <ReviewedDate date={frontmatter.lastReviewed} />

        <div
          className="explainer-body"
          dangerouslySetInnerHTML={{ __html: html }}
        />

        <StoreButtons
          campaign={frontmatter.campaign}
          className="flex-col sm:flex-row mt-6"
        />

        <Sources sources={frontmatter.sources} />
        <RelatedGuides slugs={frontmatter.related} navItems={navItems} />
        <Disclaimer />
      </ExplainerShell>
    </Layout>
  );
};

export const explainerQuery = graphql`
  query ($id: String) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        metaTitle
        metaDescription
        datePublished
        lastReviewed
        campaign
        faq
        related
        sources {
          title
          url
        }
      }
    }
  }
`;

export default ExplainerPage;
